<template>
  <section class="order-info-dialog">
    <transition name="overlay-fade">
      <div class="overlay-message" v-if="loading">
        <p>Orderbon wordt geladen</p>
        <bouncing-spinner></bouncing-spinner>
      </div>
    </transition>
    <close-button class="close-button" @click="$emit('close')"></close-button>
    <header class="dialog-header">
      <h1>
        Opdracht {{ order.orderId }}
        <button
          v-if="hasPDF"
          class="white-pill-m download-btn"
          @click="downloadPDF(order.orderId)"
        >
          Download
        </button>
      </h1>
    </header>
    <main class="dialog-content">
      <table class="order-info block">
        <colgroup>
          <col style="width: 20%;" />
          <col style="width: 30%;" />
          <col style="width: 20%;" />
          <col style="width: 30%;" />
        </colgroup>
        <tbody>
          <tr>
            <td><h3>Ordernummer</h3></td>
            <td>
              {{ order.orderId }}
            </td>
            <td><h3>Vestiging</h3></td>
            <td v-if="order.shop">{{ order.shop.name }}</td>
          </tr>
          <tr>
            <td><h3>Orderdatum</h3></td>
            <td>{{ $filters.formatDate(order.orderDate) }}</td>
            <td></td>
            <td v-if="order.shop">Tel: {{ order.shop.phone }}</td>
          </tr>
          <tr>
            <td><h3>Opleverdatum</h3></td>
            <td>{{ $filters.formatDate(order.deliveryDate) }}</td>
            <td></td>
            <td v-if="order.shop">{{ order.shop.email }}</td>
          </tr>
          <tr>
            <td><h3>Status</h3></td>
            <td>{{ order.orderStatus }}</td>
            <td></td>
            <td></td>
          </tr>
          <tr>
            <td height="15px"></td>
            <td></td>
            <td></td>
            <td></td>
          </tr>
          <tr>
            <td><h3>Opdrachtgever</h3></td>
            <td v-if="order.invoiceAddress?.company">
              {{ order.invoiceAddress.company }}
            </td>
            <td v-else></td>
            <td>
              <h3>Geplaatst via</h3>
            </td>
            <td>{{ orderSource }}</td>
          </tr>
          <tr>
            <td></td>
            <td>{{ order.orderedBy }}</td>

            <td><h3 v-if="orderType">Type order</h3></td>
            <td>{{ orderType }}</td>
          </tr>
          <tr>
            <td>
              <h3>
                <span v-show="order.signedName">Ontvanger</span>
              </h3>
            </td>
            <td>
              <span v-show="order.signedName">{{ order.signedName }}</span>
            </td>
            <td></td>
            <td></td>
          </tr>

          <tr>
            <td></td>
            <td>
              <button
                v-show="order.signedName"
                class="white-pill-m sign-btn"
                @click="viewSigned(order.orderId)"
              >
                Bekijk handtekening
              </button>
            </td>
            <td></td>
            <td></td>
          </tr>
        </tbody>
      </table>

      <section
        class="block addresses"
        :class="{ 'col-3': order.pickupAddress }"
      >
        <div
          v-if="orderSource === 'E-mail' || orderSource === 'Dalux'"
          class="email-customer"
        >
          <h3>Factuurgegevens</h3>
          <p>Klantnummer: {{ order.customerNumber }}</p>
          <p>Klantnaam: {{ order.customerName }}</p>
        </div>
        <div v-else class="invoice-address">
          <h3>{{ factuurTitle }}</h3>
          <p>Klantnummer: {{ order.customerNumber }}</p>
          <p>{{ order.invoiceAddress.company }}</p>

          <p v-if="order.project">
            <span class=""> {{ projectTitle }}: </span>

            {{ order.project }}
          </p>

          <p v-if="order.reference">
            <span class="">
              {{ referenceTitle }}
            </span>
            {{ order.reference }}
          </p>
        </div>

        <div class="pickup-address" v-if="order.pickupAddress">
          <h3>Ophaaladres</h3>
          <p>{{ order.pickupAddress.company }}</p>
          <p v-if="order.pickupAddress.department">
            {{ order.pickupAddress.department }}
          </p>
          <p v-if="order.pickupAddress.name">
            t.a.v. {{ order.pickupAddress.name }}
          </p>
          <p>
            {{ order.pickupAddress.street }} {{ order.pickupAddress.number
            }}{{ order.pickupAddress.addition }}
          </p>
          <p>
            {{ order.pickupAddress.zipCode }} {{ order.pickupAddress.city }}
          </p>
        </div>

        <div class="delivery-address" v-if="order.deliveryAddress">
          <h3>Bezorgadres</h3>
          <p>{{ order.deliveryAddress.company }}</p>
          <p v-if="order.deliveryAddress.department">
            {{ order.deliveryAddress.department }}
          </p>
          <p v-if="order.deliveryAddress.name">
            t.a.v. {{ order.deliveryAddress.name }}
          </p>
          <p>
            {{ order.deliveryAddress.street }} {{ order.deliveryAddress.number
            }}{{ order.deliveryAddress.addition }}
          </p>
          <p>
            {{ order.deliveryAddress.zipCode }}
            {{ order.deliveryAddress.city }}
          </p>
        </div>
      </section>

      <scan-order
        class="block"
        :order="order"
        v-if="orderType === 'Scannen'"
      ></scan-order>

      <instruction
        class="block"
        :instruction="order.instruction"
        v-if="order.instruction"
      ></instruction>

      <print-order-files class="block" :files="order.files" v-if="showFiles">
      </print-order-files>

      <print-store-products
        v-if="orderType === 'PrintStore'"
        :products="order.items"
      ></print-store-products>
    </main>
  </section>
</template>

<script>
import { getOrder } from '@/features/filestore/FileStoreApi'
import ScanOrder from './ScanOrder.vue'
import Instruction from './Instruction.vue'
import PrintOrderFiles from './PrintOrderFiles.vue'
import PrintStoreProducts from './PrintStoreProducts.vue'
import TemplateSpecs from '@/features/printOrder/children/listitem/TemplateSpecs.vue'

export default {
  name: 'OrderInfo',
  props: ['orderId'],
  data() {
    return {
      order: {
        shop: {},
        orderedBy: {},
        deliveryAddress: {
          name: '',
          department: ''
        },
        pickupAddress: {
          name: '',
          department: ''
        },
        invoiceAddress: {
          name: '',
          department: ''
        },
        files: []
      },
      loading: true
    }
  },
  components: {
    TemplateSpecs,
    ScanOrder,
    Instruction,
    PrintOrderFiles,
    PrintStoreProducts
  },
  computed: {
    hasPDF() {
      return (
        (this.orderType === 'Printen' ||
          this.orderType === 'Secured printen') &&
        this.orderSource != 'E-mail'
      )
    },
    showFiles() {
      return ['Printen', 'Secure printen', 'CUSTOM'].includes(
        this.order.orderType
      )
    },
    custom() {
      return this.rootStore.theme.customFields
    },
    orderType() {
      if (this.order.orderType === 'CUSTOM' && this.order.secure) {
        return 'Secured printen'
      } else if (this.order.orderType === 'CUSTOM') {
        return 'Printen'
      } else if (this.order.orderType === 'STORE') {
        return 'PrintStore'
      } else if (this.order.orderType === 'SCAN') {
        return 'Scannen'
      } else if (this.order.orderType === 'COPY') {
        return 'Kopiëren'
      } else if (this.order.orderType === 'DALUX') {
        return 'Dalux'
      } else {
        return ''
      }
    },
    orderSource() {
      if (this.order.sourceSystem === 'WEB') {
        return 'Rijnja.nl'
      } else if (this.order.sourceSystem === 'OUTLOOK') {
        return 'E-mail'
      } else if (this.order.sourceSystem === 'DALUX') {
        return 'Dalux'
      } else {
        return ''
      }
    },
    referenceTitle() {
      return this.custom.reference && this.custom.reference.label
        ? this.custom.reference.label
        : 'Orderreferentie'
    },
    projectTitle() {
      return this.custom.project && this.custom.project.label
        ? this.custom.project.label
        : 'Project'
    },
    factuurTitle() {
      return this.custom.factuuradres && this.custom.factuuradres.label
        ? this.custom.factuuradres.label
        : 'Factuurgegevens'
    }
  },
  methods: {
    downloadPDF(id) {
      location.href = `/api/v3/order-confirmation/${id}`
    },
    viewSigned(id) {
      location.href = '/api/v1/signature.php?orderId=' + id
    }
  },
  created() {
    getOrder(this.orderId).then((order) => {
      this.order = order
      this.loading = false
    })
  }
}
</script>
<style lang="scss" scoped>
.addresses.block {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;

  > div {
    flex-basis: 50%;
  }

  &.col-3 {
    > div {
      flex-basis: 32%;
    }
  }

  h3 {
    margin-bottom: 5px;
  }

  p {
    padding: 0;
  }
}
.download-btn {
  margin-left: 10px;
}

.order-info-dialog {
  overflow: auto;
  height: 100%;
}

.block {
  padding-bottom: 15px;
}
table {
  width: 100%;
}

.address {
  line-height: 21px;
  span {
    display: block;
  }
}

td {
  padding-bottom: 2px;
}
h3 {
  font-weight: 600;
}

.sign-btn {
  margin-left: -7px;
}

th {
  text-align: left;
}

.col-3 {
  th {
    width: 33.3%;
  }
}

.col-2 {
  th {
    width: 50%;
  }
}

.dialog-content {
  .address-info {
    th {
      // padding-bottom: 4px;
    }
    p {
      //  padding-bottom: 2px;
    }
  }
}
</style>
